import React from "react";
import { Check } from "lucide-react";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import Photo from "../components/site/Photo";
import ScrollReveal from "../components/site/ScrollReveal";
import {
  PlannerBlock, MelbourneSection, FAQSection, FinalCTA, Breadcrumb, SisterServices,
} from "../components/site/ServiceSections";
import InternalRail from "../components/site/InternalRail";
import { blogsForService } from "../data/internalLinks";

export default function VenuePhotography() {
  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />
      <Hero />
      <WhyMatters />
      <RoomSpread />
      <Categories />
      <PlannerBlock
        eyebrow="Plan it now · Interactive estimator"
        headline="Combine venue and food"
        italic="in one shoot day."
        blurb="Most venue shoots are bolted onto a menu or food day — same lighting, same team, half the cost. Use the planner to scope it, we'll confirm on the call."
      />
      <MelbourneSection
        serviceLabel="Venue Photography"
        eyebrow="Melbourne hospitality · Mobile coverage"
        headline="A venue photographer for"
        italic="Melbourne dining rooms."
        intro="Front-of-house, fit-out and ambience photography for restaurants, cafés, bars and hotels across Greater Melbourne — CBD function rooms, Brunswick wine bars, Carlton trattorias, Richmond rooftops, Glen Waverley and Dandenong family restaurants."
      />
      <SisterServices exclude="venue" />
      <InternalRail
        eyebrow="Related reading · The Journal"
        headline="More on venue"
        italic="and Google photography."
        theme="ink"
        cols={3}
        items={blogsForService("venue-photography")}
      />
      <FAQSection
        eyebrow="Common questions · Venue photography Melbourne"
        headline="Lighting, timing,"
        italic="and access."
        intro="If your room is dim, narrow, north-facing or only beautiful at dusk — that's a normal hospitality venue. We light around it. Most rooms are shot during service or just before doors."
        faqs={VENUE_FAQS}
      />
      <FinalCTA
        eyebrow="Final step · Hand-confirmed quotes"
        headline="Ready to shoot"
        italic="your dining room?"
        intro="Quick form below or call directly — quotes confirmed inside one business day. We can also walk the room with you first if it helps."
        backdropSlug="venue-buffet-01"
        formTitle="Tell us about your venue shoot."
      />
      <Footer />
    </div>
  );
}

const Hero = () => (
  <section className="relative min-h-[100svh] overflow-hidden bg-ink">
    <div className="absolute inset-0">
      <Photo slug="hero-banquet-01" alt="Venue photography Melbourne — restaurant interior" priority ratio="auto" className="!relative h-full w-full" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-b from-ink/75 via-ink/40 to-ink" />
    <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/30 to-transparent" />

    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 min-h-[100svh] flex flex-col pt-24 sm:pt-28 lg:pt-32 pb-10 lg:pb-12">
      <Breadcrumb current="Venue Photography Melbourne" />

      <div className="flex-1 flex flex-col justify-center max-w-5xl">
        <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 sm:mb-7 font-bold">
          Venue Photography Melbourne · Restaurants · Bars · Cafés · Hotels
        </p>

        <h1 className="font-display tracking-editorial leading-[0.92]" style={{ fontSize: "clamp(2.5rem, 7.5vw, 6.5rem)" }}>
          A dining room that
          <br />
          looks <span className="italic font-light text-ember">like it feels.</span>
        </h1>

        <p className="mt-6 sm:mt-8 max-w-2xl text-base sm:text-lg md:text-xl leading-relaxed text-bone/85">
          Interior and venue photography for Melbourne restaurants, cafés, bars
          and hotels. Front of house, fit-out detail, open kitchens and real
          service moments — shot in your room, lit to match the mood guests
          actually walk into.
        </p>

        <p className="mt-7 max-w-xl">
          <span className="inline-block bg-ember text-bone font-bold text-sm md:text-base uppercase tracking-wider2 px-4 py-2.5">
            Venue Shoots From $450
          </span>
        </p>
        <p className="mt-3 text-xs text-bone/55 max-w-md leading-relaxed">
          Short on-location session — combines beautifully with a menu day.
          Full editorial venue features quoted separately.
        </p>

        <div className="mt-8 sm:mt-10 flex flex-wrap gap-3">
          <a href="#enquiry" className="w-full sm:w-auto">
            <CTAButton variant="primary" size="lg" className="w-full sm:w-auto">Get A Venue Quote</CTAButton>
          </a>
          <a href="#planner" className="w-full sm:w-auto">
            <CTAButton variant="ghost" size="lg" arrow={false} className="w-full sm:w-auto">Plan The Shoot</CTAButton>
          </a>
        </div>
      </div>

      <div className="mt-10 lg:mt-12 grid grid-cols-3 gap-4 md:gap-10 max-w-3xl border-t border-bone/15 pt-6">
        {[
          ["Session", "2–4 hours"],
          ["Turnaround", "5–7 days"],
          ["Coverage", "All Melbourne"],
        ].map(([k, v]) => (
          <div key={k}>
            <p className="text-[10px] sm:text-xs uppercase tracking-wider2 text-ember mb-1.5 font-bold">{k}</p>
            <p className="font-sans font-bold text-base sm:text-lg md:text-xl leading-tight tracking-tight">{v}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const WhyMatters = () => (
  <section className="relative bg-ink text-bone py-24 lg:py-36 overflow-hidden">
    <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Why venue photography Melbourne operators invest in
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Guests book the room
            <br />
            <span className="italic font-light text-ember">before they book the food.</span>
          </h2>

          <div className="mt-12 grid gap-7 text-bone/85 leading-relaxed text-base md:text-lg">
            <ScrollReveal>
              <p>
                A venue photo on Google, on your Instagram, on a function
                enquiry deck — that's the photograph that decides whether the
                booking is yours or the place around the corner. The
                <strong className="text-bone"> room is the first menu</strong> a guest reads.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={120}>
              <p>
                We shoot real service — guests at the table, candles lit, glass
                in hand. No bright fluorescent fly-throughs and no awkwardly
                empty rooms. Where it helps, we'll light a corner to match
                what the room actually does at 7:30pm.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <p>
                Strongest applications: function/event enquiries, hotel
                listings, Google Business cover imagery, website hero,
                investor decks and PR. Pair with a menu day for the cheapest
                full library you'll get.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={280}>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 pt-4">
                {[
                  "Front-of-house & dining room",
                  "Fit-out & architectural detail",
                  "Open kitchens & passes",
                  "Bar, counter & wine wall",
                  "Outdoor seating & courtyards",
                  "Service moments with guests",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <Check size={16} strokeWidth={2} className="text-ember mt-1 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </ScrollReveal>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5 space-y-3 md:space-y-5 lg:mt-4">
          <Photo slug="venue-buffet-01" alt="Restaurant venue photography Melbourne — buffet detail" ratio="4/5" sizes="(min-width: 1024px) 35vw, 100vw" />
          <div className="grid grid-cols-2 gap-3 md:gap-5">
            <Photo slug="hero-banquet-01" alt="Dining room photography Melbourne" ratio="1/1" sizes="20vw" />
            <Photo slug="hero-spread-01" alt="Restaurant interior photography Melbourne" ratio="1/1" sizes="20vw" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

const RoomSpread = () => (
  <section className="bg-bone text-ink py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-12 mb-14 lg:mb-16 items-end">
        <div className="col-span-12 lg:col-span-6">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Editorial · The room as a portrait
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            One room.
            <br />
            <span className="italic font-light text-ember">A whole brand.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-6 lg:pb-3">
          <p className="text-char text-base md:text-lg leading-relaxed">
            A good venue photograph reads in two ways at once — the wide,
            architectural sense of the room, and the small, human detail you
            only notice second. Wine glass condensation. A staff member
            mid-pour. The candle. We shoot for both.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-3 md:gap-5">
        <div className="col-span-12 md:col-span-7">
          <Photo slug="venue-buffet-01" alt="Editorial venue photography Melbourne" ratio="16/10" sizes="(min-width: 768px) 55vw, 100vw" />
        </div>
        <div className="col-span-12 md:col-span-5 grid grid-cols-2 md:grid-cols-1 gap-3 md:gap-5">
          <Photo slug="hero-banquet-01" alt="Dining table photography Melbourne" ratio="1/1" sizes="20vw" />
          <Photo slug="hero-spread-01" alt="Restaurant detail photography Melbourne" ratio="1/1" sizes="20vw" />
        </div>
      </div>
    </div>
  </section>
);

const CATEGORIES = [
  { n: "01", title: "Dining room", slug: "hero-banquet-01", desc: "The wide, room-defining shot — fit-out, tables, light. Built for Google, website hero, function enquiries." },
  { n: "02", title: "Bar & counter", slug: "venue-buffet-01", desc: "The bar at service, glassware, pour shots, the wall behind it. Critical for cocktail bars and wine-led venues." },
  { n: "03", title: "Open kitchen", slug: "hero-spread-01", desc: "Pass, plating, flame and steam — the working heart of the restaurant. Real chefs, real service, no staging." },
  { n: "04", title: "Service moments", slug: "hero-banquet-01", desc: "Guests at the table, hands and glasses in frame, dim ambient light. The most-used image in any venue's library." },
  { n: "05", title: "Fit-out detail", slug: "hero-banquet-01", desc: "Tiles, brass, joinery, signage — the design language behind the room. Useful for PR and architectural press." },
  { n: "06", title: "Outdoor seating", slug: "hero-spread-01", desc: "Courtyards, laneway seating, rooftop bars. Shot in your golden hour, the way guests post about you." },
  { n: "07", title: "Function spaces", slug: "venue-buffet-01", desc: "Private dining, function rooms and event setups — staged the way you actually sell them in enquiry decks." },
  { n: "08", title: "Brand details", slug: "menu-malai-tikka", desc: "Logo on signage, menu print, napkin embroidery, custom plate, in-room print runs — your identity in the room." },
];

const Categories = () => (
  <section className="bg-ink text-bone py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 mb-14 lg:mb-20 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            What we photograph · Venue & interior
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Every angle of
            <br />
            <span className="italic font-light text-ember">your room.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-bone/75 text-base md:text-lg leading-relaxed max-w-md">
            Wide architectural frames, intimate service moments, all the
            in-between detail. One session covers Google, web, social, PR and
            function enquiry decks.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-bone/10 border border-bone/10">
        {CATEGORIES.map((c) => (
          <article key={c.n} className="bg-ink hover:bg-[#161613] transition-colors p-5 md:p-6 flex flex-col">
            <Photo slug={c.slug} alt={`${c.title} — Venue photography Melbourne`} ratio="4/5" sizes="(min-width: 1024px) 22vw, 50vw" />
            <div className="mt-5 flex flex-col flex-1">
              <span className="font-display italic text-2xl text-ember leading-none">{c.n}</span>
              <h3 className="font-display text-xl md:text-2xl tracking-editorial leading-tight mt-2.5">{c.title}</h3>
              <p className="mt-3 text-sm text-bone/70 leading-relaxed flex-1">{c.desc}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const VENUE_FAQS = [
  { q: "When is the best time to shoot the venue?", a: "Whatever time the room looks best to your guests. Most dim restaurants shoot 30 minutes before doors with all service lighting on; cafés and brunch venues shoot in working daylight. We'll walk you through the right window on the call." },
  { q: "Do we need to close the venue for the shoot?", a: "Almost never. We work around service. For service-moment shots we'll either bring a small group of friendly faces or shoot during a quieter pre-service window with staff at the tables. Discrete, no disruption." },
  { q: "Can we combine venue with a menu or food shoot?", a: "Yes — and this is usually the cheapest way to get a complete library. A standard menu day with a 2-hour venue add-on covers dishes, room, bar and a handful of service moments in a single visit." },
  { q: "Do you need plans, lighting diagrams or models?", a: "No plans needed. We bring full lighting kit and shoot to taste. If you want featured staff or guests in frame we'll send a short brief — otherwise we shoot whoever's on shift. No agency models, no staged bodies." },
  { q: "Can the photos be used for Google Business, real estate listings and function decks?", a: "Yes. The licence covers all owned and earned channels — Google Business, website, social, function/event enquiry decks, real-estate listings (for sale or lease), and PR. Print and outdoor included." },
  { q: "What if my space looks bad in photos right now?", a: "That's almost always a lighting problem, not a fit-out problem. We light the room properly and it usually transforms. If genuinely tired, we'll be honest on the call and suggest the smallest fix that makes the biggest difference." },
];
